import * as v2 from '@/core/services/api/v2';

const state = {
  competitions: [],
  rankings: {},
  timeLeft: {}
};

const mutations = {
  SET_COMPETITIONS(state, competitions) {
    state.competitions = competitions;
  },
  SET_RANKING(state, { competitionId, ranking }) {
    state.rankings = {
      ...state.rankings,
      [competitionId]: ranking
    };
  },
  SET_TIME_LEFT(state, { competitionId, timeLeft }) {
    state.timeLeft = {
      ...state.timeLeft,
      [competitionId]: timeLeft
    };
  }
};

const actions = {
  async fetchAllCompetitions({ commit }) {
    try {
      const competitions = await v2.competicoes();
      commit('SET_COMPETITIONS', competitions);

      // Inicializa os timers para cada competição. O V2 não manda serverTime
      // na resposta — o relógio local faz as vezes dele; a apuração de quem
      // ganhou continua sendo do servidor, o timer aqui é só exibição.
      const serverTime = new Date().toISOString();
      competitions.forEach(competition => {
        commit('SET_TIME_LEFT', {
          competitionId: competition.id,
          timeLeft: calculateTimeLeft(serverTime, competition.initDate, competition.endDate)
        });
      });

      return competitions;
    } catch (error) {
      console.error(
        'Error fetching competitions:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message
      );
      return [];
    }
  },

  async fetchCompetitionRanking({ commit }, competitionId) {
    try {
      // No V2 o ranking é **por competição** — o Strapi devolvia o mesmo
      // top10 global para todas, e o `competitionId` só indexava o cache.
      const ranking = await v2.rankingDaCompeticao(competitionId);
      commit('SET_RANKING', {
        competitionId,
        ranking
      });
      return ranking;
    } catch (error) {
      console.error(
        'Error fetching ranking:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message
      );
      return [];
    }
  },

  /**
   * Inscreve o jogador numa competição.
   *
   * **A UI não chama isto, e não deve.** Desde 11/09/2026 quem inscreve é o
   * servidor, na captura (`garantirInscricoes`, no caminho de pesca): pescar
   * participa, como era no Strapi. O comentário que estava aqui — "ainda não
   * tem chamador na UI, mas a action já existe para a tela que vier" — descrevia
   * o bug inteiro: a tela nunca veio, ninguém nunca se inscreveu, e o quadro de
   * honra ficou vazio para todo jogador humano enquanto o cartaz prometia
   * "pesque agora e abra o quadro".
   *
   * A action fica porque a rota fica — o cérebro dos bots usa a mesma. E o
   * backend agora recusa competição encerrada ou que ainda não largou, que era
   * a outra metade do buraco.
   */
  async joinCompetition({ rootState }, competitionId) {
    try {
      await v2.inscreverNaCompeticao(rootState.player?.id, competitionId);
      return { success: true };
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data?.error?.message || error.message;
      console.error('Error joining competition:', message);
      return { success: false, error: message };
    }
  },

  updateTimeLeft({ commit }, { competitionId, serverTime, initDate, endDate }) {
    const now = new Date(serverTime).getTime();
    const start = new Date(initDate).getTime();
    const end = new Date(endDate).getTime();

    let timeLeft;
    if (now < start) {
      timeLeft = {
        type: 'start',
        seconds: Math.floor((start - now) / 1000)
      };
    } else if (now < end) {
      timeLeft = {
        type: 'end',
        seconds: Math.floor((end - now) / 1000)
      };
    } else {
      timeLeft = {
        type: 'finished',
        seconds: 0
      };
    }

    commit('SET_TIME_LEFT', { competitionId, timeLeft });
  }
};

const calculateTimeLeft = (serverTime, initDate, endDate) => {
  const now = new Date(serverTime).getTime();
  const start = new Date(initDate).getTime();
  const end = new Date(endDate).getTime();

  if (now < start) {
    return {
      type: 'start',
      seconds: Math.floor((start - now) / 1000)
    };
  } else if (now < end) {
    return {
      type: 'end',
      seconds: Math.floor((end - now) / 1000)
    };
  } else {
    return {
      type: 'finished',
      seconds: 0
    };
  }
};

const getters = {
  getCompetitionTimeLeft: (state) => (competitionId) => {
    return state.timeLeft[competitionId];
  },
  getCompetitionRanking: (state) => (competitionId) => {
    return state.rankings[competitionId] || [];
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
